"use client";

import { createContext, useContext, useEffect, useState } from "react";

/**
 * What a page may put in the side nav's panel:
 *
 *   • `topAction` — a control above the links (the admin area's Build content
 *     launcher).
 *   • `outline`   — the page's own table of contents, shown instead of the
 *     route's menu while the page is open: `{ title, links, activeHrefs }`,
 *     with `links` in the manifest's item vocabulary (`label`, `href`, `icon`
 *     or `iconName`, `subItems`, `count`) and `activeHrefs` from the page's
 *     scroll spy. The playbook registers one.
 *
 * The provider sits in SideNav; a page registers with <SideNavSlot>. Registering
 * is an effect, so it clears when the page unmounts, and the registrar renders
 * nothing of its own.
 */

const EMPTY = Object.freeze({ topAction: null, outline: null });
const SlotsContext = createContext(EMPTY);
const SetSlotsContext = createContext(null);

export function SideNavSlotsProvider({ children }) {
  const [slots, setSlots] = useState(EMPTY);
  return (
    <SetSlotsContext.Provider value={setSlots}>
      <SlotsContext.Provider value={slots}>{children}</SlotsContext.Provider>
    </SetSlotsContext.Provider>
  );
}

export function useSideNavSlots() {
  return useContext(SlotsContext);
}

/** Put something in the panel for as long as this is mounted. */
export function SideNavSlot({ topAction, outline }) {
  // Only the setter is read here, so a slot change never re-renders the
  // registrar — which is what keeps a page that rebuilds its outline on every
  // render from registering in a loop.
  const setSlots = useContext(SetSlotsContext);

  useEffect(() => {
    if (!setSlots) return undefined;
    const provided = {};
    if (topAction !== undefined) provided.topAction = topAction;
    if (outline !== undefined) provided.outline = outline;

    setSlots((prev) => ({ ...prev, ...provided }));
    return () => {
      const cleared = Object.fromEntries(
        Object.keys(provided).map((key) => [key, null]),
      );
      setSlots((prev) => ({ ...prev, ...cleared }));
    };
  }, [setSlots, topAction, outline]);

  return null;
}
